import { useCallback, useEffect, useRef, useState } from "react";
import {
    getEndpointUserAssignments,
    getEndpointManualAssignment,
    setEndpointManualAssignment,
    removeEndpointManualAssignment,
    listDirectoryUsers,
    providerLabel,
} from "../../lib/identityClient.js";
import { newOperationId } from "../../lib/accessClient.js";
import { authorizationContextKey, hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { NOTE_CLASS } from "../../lib/ui.js";

const BUTTON = "rounded-[2px] border border-[color:var(--line)] px-2 py-1 text-[11px] disabled:opacity-40";
const INPUT = "min-h-[32px] w-full border border-[color:var(--line)] bg-transparent px-2 text-[12px] text-[color:var(--ink)]";
// The final check extends beyond the five-minute owner-cache expiry window.
const POLL_DELAYS_MS = [5000, 15000, 30000, 60000, 120000, 120000];

function userKey(user) {
    return JSON.stringify([user?.integrationId, user?.sourceProvider, user?.sourceId]);
}

function DirectoryIdentity({ user }) {
    return (
        <span className="grid gap-1 break-all">
            <span>{user.userDisplayName || user.displayName || user.userEmail || user.email || user.sourceId}</span>
            {(user.userEmail || user.email) && <span className="text-[color:var(--muted)]">{user.userEmail || user.email}</span>}
            <span className="text-[10px] text-[color:var(--muted)]">
                {providerLabel(user.sourceProvider)} / Integration: {user.integrationId} / User ID: {user.sourceId}
            </span>
        </span>
    );
}

function DirectoryUserPicker({ selected, onSelect, disabled, caps }) {
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState({ search: "", token: "", page: 1 });
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const sequence = useRef(0);
    useEffect(() => {
        const request = ++sequence.current;
        setLoading(true);
        setError(null);
        setResult(null);
        const load = () => listDirectoryUsers({ search: query.search, page_size: 20, page_token: query.token, active_only: true, primary_directory_only: true })
            .then((page) => {
                if (request !== sequence.current) return;
                if (page.primaryDirectoryOnly !== true) throw new Error("Primary-directory search is unavailable. Try again shortly.");
                setResult(page);
            })
            .catch((cause) => { if (request === sequence.current) setError(cause); })
            .finally(() => { if (request === sequence.current) setLoading(false); });
        const timer = query.debounce ? setTimeout(load, 250) : null;
        if (!query.debounce) load();
        return () => { sequence.current++; clearTimeout(timer); };
    }, [query]);

    function searchChanged(value) {
        sequence.current++;
        setSearch(value);
        setResult(null);
        setError(null);
        setLoading(true);
        onSelect(null);
        setQuery({ search: value.trim(), token: "", page: 1, debounce: true });
    }

    function goToPage(next) {
        onSelect(null);
        setQuery(next);
    }

    return (
        <div className="grid gap-2">
            <form onSubmit={(event) => {
                event.preventDefault();
                if (!disabled) goToPage({ search: search.trim(), token: "", page: 1 });
            }} className="flex gap-2">
                <input
                    type="search"
                    aria-label="Search active directory users"
                    placeholder="Search name or email"
                    value={search}
                    autoFocus
                    disabled={disabled}
                    onChange={(event) => searchChanged(event.target.value)}
                    className={INPUT}
                />
                <button className={BUTTON} type="submit" disabled={disabled}>Search</button>
            </form>
            <p className="text-[11px] text-[color:var(--muted)]">Search all active users in the primary directory.</p>
            <div aria-label="Directory user results" aria-busy={loading} className="grid max-h-[260px] gap-1 overflow-y-auto">
                {loading && <p>Searching directory users...</p>}
                {error && <p role="alert">Could not search users. {denialOrRawMessage(error, caps)} Search again to retry.</p>}
                {result && !result.users?.length && <p>No active users match this search in the primary directory.</p>}
                {result?.users?.map((user) => {
                    const eligible = user.accountEnabled === true && user.integrationId && user.sourceProvider && user.sourceId;
                    return (
                        <button
                            key={userKey(user)}
                            type="button"
                            disabled={disabled || !eligible}
                            aria-pressed={selected ? userKey(selected) === userKey(user) : false}
                            onClick={() => onSelect(user)}
                            className={`${BUTTON} text-left aria-pressed:border-[color:var(--green)] aria-pressed:bg-[color:var(--green-soft)]`}
                        >
                            <DirectoryIdentity user={user} />
                            {!eligible && <span>Unavailable for assignment</span>}
                        </button>
                    );
                })}
            </div>
            {result && <div className="flex items-center gap-2">
                {query.page > 1 && <button type="button" className={BUTTON} disabled={disabled || loading} onClick={() => goToPage({ search: query.search, token: "", page: 1 })}>First page</button>}
                <span className="text-[10px] text-[color:var(--muted)]">Page {query.page}</span>
                {result.nextPageToken && <button type="button" className={BUTTON} disabled={disabled || loading} onClick={() => goToPage({ search: query.search, token: result.nextPageToken, page: query.page + 1 })}>Next page</button>}
            </div>}
        </div>
    );
}

function statusText(snapshot, endpointRevoked) {
    if (endpointRevoked && snapshot.assignment) return "Inactive. This endpoint has been revoked.";
    switch (snapshot.status) {
        case "none": return "No manual assignment.";
        case "pending": return snapshot.assignment
            ? "Saved. Waiting for the assignment to take effect."
            : "Removal saved. Waiting for the previous assignment to stop taking effect.";
        case "effective": return "Applied at the last ownership refresh. This user supplies the endpoint's fallback identity when automatic matching has no result.";
        case "automatic": return "Automatic matching currently takes precedence over the saved fallback. Activity processing can take up to 5 minutes to reflect this change.";
        case "conflict": return "Automatic matching found conflicting users. The manual assignment cannot resolve this conflict. Activity processing can take up to 5 minutes to reflect this change.";
        case "ineligible": return "The saved user is unavailable for assignment. Check the directory account, choose another user, or remove the fallback.";
        default: return "Assignment availability could not be confirmed. Refresh to check again.";
    }
}

function ManualAssignment({ endpointId, endpointLabel, isRevoked, caps, assignmentsKnown, onChanged }) {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [writeError, setWriteError] = useState(null);
    const [polls, setPolls] = useState(0);
    const mounted = useRef(false);
    const sequence = useRef(0);
    const writing = useRef(false);
    const operation = useRef(null);
    const hasSnapshot = useRef(false);
    const endpointRevoked = isRevoked || snapshot?.endpointRevoked === true;

    const refresh = useCallback(async () => {
        if (writing.current) return;
        const request = ++sequence.current;
        setLoading(true);
        setError(null);
        try {
            const next = await getEndpointManualAssignment(endpointId);
            if (mounted.current && request === sequence.current) {
                setSnapshot(next);
                setWriteError(null);
                if (hasSnapshot.current) onChanged();
                hasSnapshot.current = true;
            }
        } catch (cause) {
            if (mounted.current && request === sequence.current) setError(cause);
        } finally {
            if (mounted.current && request === sequence.current) setLoading(false);
        }
    }, [endpointId, onChanged]);

    useEffect(() => {
        mounted.current = true;
        refresh();
        return () => { mounted.current = false; sequence.current++; };
    }, [refresh]);

    useEffect(() => {
        if (snapshot?.status !== "pending" || loading || saving || error || polls >= POLL_DELAYS_MS.length) return;
        const timer = setTimeout(() => {
            setPolls((count) => count + 1);
            refresh();
        }, POLL_DELAYS_MS[polls]);
        return () => clearTimeout(timer);
    }, [snapshot?.status, loading, saving, error, polls, refresh]);

    useEffect(() => {
        if (endpointRevoked) {
            setEditing(false);
            setSelected(null);
        }
    }, [endpointRevoked]);

    async function mutate(kind) {
        if (writing.current || loading || error || !snapshot) return;
        if (kind === "set" && (endpointRevoked || !selected || (!snapshot.assignment && (!snapshot.canAssign || !assignmentsKnown)))) return;
        if (kind === "set" && snapshot.assignment && userKey(selected) === userKey(snapshot.assignment)) return;
        if (kind === "remove" && !snapshot.assignment) return;
        const key = JSON.stringify([kind, snapshot.revision, kind === "set" ? userKey(selected) : null]);
        if (operation.current?.key !== key) operation.current = { key, requestId: newOperationId() };
        writing.current = true;
        sequence.current++;
        setSaving(true);
        setWriteError(null);
        try {
            const args = { expectedRevision: snapshot.revision, requestId: operation.current.requestId };
            const next = kind === "set"
                ? await setEndpointManualAssignment(endpointId, { ...args, user: selected })
                : await removeEndpointManualAssignment(endpointId, args);
            if (!mounted.current) return;
            setSnapshot(next);
            setEditing(false);
            setSelected(null);
            setPolls(0);
            operation.current = null;
            onChanged();
        } catch (cause) {
            if (mounted.current) setWriteError(cause);
        } finally {
            writing.current = false;
            if (mounted.current) setSaving(false);
        }
    }

    const canRemove = snapshot?.assignment && !loading && !error && !saving;
    const canEdit = snapshot && !loading && !error && !saving && !endpointRevoked
        && (snapshot.assignment || (snapshot.canAssign && assignmentsKnown));
    return (
        <section aria-label="Manual assignment" aria-busy={loading || saving} className="grid gap-2 border-t border-[color:var(--line)] pt-2 text-[12px]">
            <div className="flex items-center justify-between gap-2">
                <h4 className="font-medium">Manual fallback</h4>
                <button type="button" className={BUTTON} disabled={loading || saving} onClick={() => { setPolls(0); refresh(); }}>Refresh assignment</button>
            </div>
            {loading && !snapshot && <p>Loading manual assignment...</p>}
            {error && <p role="alert">Could not confirm the assignment. {denialOrRawMessage(error, caps)} Refresh to retry.</p>}
            {snapshot && <>
                {snapshot.assignment && <DirectoryIdentity user={snapshot.assignment} />}
                {!error && <p role="status">{statusText(snapshot, endpointRevoked)}</p>}
                {snapshot.status === "pending" && polls >= POLL_DELAYS_MS.length && <p>Still pending. Refresh assignment to check progress.</p>}
                {endpointRevoked && <p>This endpoint is revoked. You can inspect or remove its saved fallback.</p>}
                {snapshot.status === "effective" && snapshot.effectiveFrom && <p className="text-[11px] text-[color:var(--muted)]">Ownership recorded from {snapshot.effectiveFrom}.</p>}
                <p className="text-[11px] text-[color:var(--muted)]">Automatic matching takes precedence. Ownership-based visibility updates when ownership is refreshed.</p>
                {!editing && <div className="flex gap-2">
                    {canEdit && <button type="button" className={BUTTON} onClick={() => { setWriteError(null); setEditing(true); }}>{snapshot.assignment ? "Replace user" : "Assign user"}</button>}
                    {snapshot.assignment && <button type="button" className={BUTTON} disabled={!canRemove} onClick={() => mutate("remove")}>Remove manual assignment</button>}
                </div>}
            </>}
            {editing && !endpointRevoked && <>
                <DirectoryUserPicker selected={selected} onSelect={setSelected} disabled={saving} caps={caps} />
                {selected && <div className="grid gap-1 border border-[color:var(--line)] p-2">
                    <p>Assign to endpoint: <strong>{endpointLabel || endpointId}</strong></p>
                    <DirectoryIdentity user={selected} />
                    <p>Uses this user only when automatic matching has no result. The assignment applies after ownership is refreshed.</p>
                </div>}
                <div className="flex gap-2">
                    <button type="button" className={BUTTON} disabled={!canEdit || !selected || (snapshot?.assignment && userKey(selected) === userKey(snapshot.assignment))} onClick={() => mutate("set")}>{saving ? "Saving..." : "Save assignment"}</button>
                    <button type="button" className={BUTTON} disabled={saving} onClick={() => { setEditing(false); setSelected(null); setWriteError(null); }}>Cancel</button>
                </div>
            </>}
            {writeError && <p role="alert">Could not save the change. {denialOrRawMessage(writeError, caps)} Retry, or refresh assignment to check the current state before editing again.</p>}
        </section>
    );
}

function assignedPeople(assignments) {
    const people = new Map();
    assignments.forEach((assignment, index) => {
        const name = assignment.userDisplayName || assignment.user_display_name;
        const email = assignment.userEmail || assignment.user_email;
        const signal = String(assignment.matchSignal || assignment.match_signal || "")
            .replace(/^(?:ENDPOINT_USER_)?MATCH_SIGNAL_/, "").toLowerCase().replace(/_/g, " ");
        const key = email ? `email:${email.trim().toLowerCase()}` : name ? `name:${name}` : `row:${index}`;
        const person = people.get(key) ?? { key, name, email, signals: [] };
        person.name ||= name;
        if (signal && signal !== "unspecified" && !person.signals.includes(signal)) person.signals.push(signal);
        people.set(key, person);
    });
    return [...people.values()];
}

function AssignmentContent({ endpointId, endpointLabel, isRevoked, caps, canManage }) {
    const [assignments, setAssignments] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const changed = useCallback(() => setRefresh((value) => value + 1), []);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        getEndpointUserAssignments(endpointId)
            .then((response) => { if (active) setAssignments(response?.assignments ?? []); })
            .catch((cause) => { if (active) setError(cause); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [endpointId, refresh]);

    return <>
        <div className="border-t border-[color:var(--line)]" />
        <section aria-label="Assigned users" className="grid gap-2">
            <h3 className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>Assigned users</h3>
            <div aria-busy={loading}>
                {loading ? <p className={`${NOTE_CLASS} text-[11px]`}>Loading...</p>
                    : error ? <div className="grid gap-2 text-[11px]">
                        <p role="alert">Could not load assigned users. {denialOrRawMessage(error, caps)}</p>
                        <button type="button" className={BUTTON} onClick={changed}>Retry assigned users</button>
                    </div>
                        : !assignments?.length ? <p className={`${NOTE_CLASS} text-[11px]`}>No assignments.</p>
                            : <ul className="grid gap-[6px]">{assignedPeople(assignments).map((person) =>
                                <li key={person.key} className="flex items-center justify-between gap-2 text-[12px]">
                                    <span className="min-w-0 truncate text-[color:var(--ink)]">
                                        {person.name || person.email || "Unknown user"}
                                        {person.name && person.email && <span className="ml-[6px] text-[color:var(--muted)]">{person.email}</span>}
                                    </span>
                                    <span className="flex shrink-0 gap-[4px]">
                                        {person.signals.map((signal) => <span key={signal} className="rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] text-[color:var(--ink)]">{signal}</span>)}
                                    </span>
                                </li>)}</ul>}
            </div>
            {canManage && <ManualAssignment endpointId={endpointId} endpointLabel={endpointLabel} isRevoked={isRevoked} caps={caps} assignmentsKnown={!loading && !error} onChanged={changed} />}
        </section>
    </>;
}

// The operational admin view can repair ownership before activity access exists.
// Its snapshot endpoint checks automatic ownership without reading captured activity.
export function EndpointAssignmentEditor({ endpointId, endpointLabel, onChanged }) {
    const caps = useMyPermissions();
    const canManage = caps?.available && !caps.loading && !caps.degraded
        && caps.permissions.has(PERMISSIONS.USERS_MANAGE)
        && caps.permissions.has(PERMISSIONS.GRANTS_MANAGE)
        && caps.permissions.has(PERMISSIONS.DIRECTORY_DATA_READ)
        && caps.bindings?.some((binding) => binding.scopeGroups?.length === 0
            && caps.roleCatalog?.some((role) => role.name === binding.role
                && role.permissions.includes(PERMISSIONS.GRANTS_MANAGE)));
    if (!canManage) return <p>You need tenant-wide access management and directory read permissions to assign this endpoint.</p>;
    return <ManualAssignment key={`${endpointId}:${authorizationContextKey(caps)}`}
        endpointId={endpointId} endpointLabel={endpointLabel} caps={caps}
        assignmentsKnown={true} onChanged={onChanged} />;
}

export default function EndpointAssignmentsSection({ endpointId, endpointLabel, isSynthetic = false, isRevoked = false }) {
    const caps = useMyPermissions();
    const tenantWideManager = !caps?.available || caps.bindings?.some((binding) =>
        binding.scopeGroups?.length === 0 && caps.roleCatalog?.some((role) =>
            role.name === binding.role && role.permissions.includes(PERMISSIONS.GRANTS_MANAGE)));
    const canManage = !isSynthetic && tenantWideManager
        && hasSensitivePermission(caps, PERMISSIONS.GRANTS_MANAGE)
        && hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    return <AssignmentContent
        key={`${endpointId}:${authorizationContextKey(caps)}:${canManage}`}
        endpointId={endpointId}
        endpointLabel={endpointLabel}
        isRevoked={isRevoked}
        caps={caps}
        canManage={canManage}
    />;
}
